import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UpdateStatus from '../../../components/common/UpdateStatus';
import AssignAgent from '../../../components/common/AssignAgent';
import ReassignAgent from '../../../components/common/ReassignAgent';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import { fetchProjectUnitSelect } from '../../../services/common';

// ─── Design tokens ─────────────────────────────────────────────────────────
const C = {
  teal:        '#0E9F7E',
  tealLight:   '#ECFDF5',
  tealMid:     '#34D399',
  tealDark:    '#065F46',
  slate:       '#0F172A',
  slate2:      '#1E293B',
  slate3:      '#64748B',
  slate4:      '#94A3B8',
  border:      '#E2E8F0',
  borderSubtle:'#F1F5F9',
  bg:          '#F8FAFC',
  white:       '#FFFFFF',
  blue:        '#2563EB',
  blueLight:   '#EFF6FF',
  blueBorder:  '#BFDBFE',
  red:         '#EF4444',
  redLight:    '#FEF2F2',
  redDark:     '#991B1B',
};

// ─── Checkmark SVG ──────────────────────────────────────────────────────────
const CheckSVG = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Edit SVG ───────────────────────────────────────────────────────────────
const EditSVG = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ─── Stage node style ────────────────────────────────────────────────────────
const getStageStyle = (seq) => {
  if (seq === 'completed') return {
    nodeColor:      C.white,
    nodeBg:         C.teal,
    nodeBorder:     C.teal,
    labelColor:     C.teal,
    labelWeight:    600,
  };
  if (seq === 'current') return {
    nodeColor:      C.white,
    nodeBg:         C.tealDark,
    // Inner ring should be white (small white ring).
    nodeBorder:     C.white,
    labelColor:     C.tealDark,
    labelWeight:    700,
    ring:           true,
  };
  return {
    nodeColor:      C.slate4,
    nodeBg:         C.white,
    nodeBorder:     C.border,
    labelColor:     C.slate4,
    labelWeight:    500,
  };
};

// ─── Main component ──────────────────────────────────────────────────────────
const LeadPipeline = ({ lead, stageStatusFlow }) => {
  const { fetchLeadDetails } = useLeadDetails();

  const currentStage       = lead?.stage        || 'New';
  const currentStatus      = lead?.status        || 'New';
  const currentStatusLabel = lead?.status_verbose || 'New';
  const currentStageOrder  = lead?.stage_order   || 1;
  const isJunk             = currentStage?.toLowerCase() === 'junk';
  const isLeadDeleted      = Boolean(lead?.is_deleted);
  const pipelineActionsLocked = isLeadDeleted || isJunk;

  const [statusDialogOpen,   setStatusDialogOpen]   = useState(false);
  const [assignAgentOpen,    setAssignAgentOpen]     = useState(false);
  const [projectUnits,       setProjectUnits]        = useState([]);

  useEffect(() => {
    fetchProjectUnitSelect()
      .then((d) => setProjectUnits(d || []))
      .catch(() => setProjectUnits([]));
  }, []);

  const agent         = lead?.agent   || null;
  const agentId       = agent?.id     || null;
  const agentName     = agent?.name   || null;
  const agentInitials = agent?.initials || (agentName ? agentName.slice(0, 2).toUpperCase() : null);

  const stages = (stageStatusFlow || []).map((item) => {
    let seq_status = 'upcoming';
    if (currentStage === item.stage)         seq_status = 'current';
    else if (item.order < currentStageOrder) seq_status = 'completed';
    return { ...item, seq_status };
  });

  return (
    <Box sx={{ bgcolor: C.white, px: '20px' }}>

      {/* ── ROW 1: Pipeline stepper ─────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          // ✅ FIX: py gives vertical room so the ring shadow isn't clipped
          py: '12px',
          // borderBottom:  `1px solid ${C.border}`,
          // ✅ FIX: overflow visible so boxShadow ring renders on edge nodes
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            flex:        1,
            display:     'flex',
            alignItems:  'center',
            // ✅ FIX: overflow visible instead of hidden — was clipping ring shadows
            overflow:    'visible',
            // ✅ FIX: horizontal padding so the ring on first/last nodes isn't cut off
            px:          '4px',
          }}
        >
          {stages.map((item, idx) => {
            const s      = getStageStyle(item.seq_status);
            const isLast = idx === stages.length - 1;

            // ✅ FIX: connector color should reflect whether this segment is "filled"
            // A segment between index i and i+1 is green when stage[i] is completed OR
            // when stage[i] is current (i.e. everything up to and including current is filled).
            // We colour it green when the LEFT node is completed.
            const connectorFilled = item.seq_status === 'completed';
            const connectorColor  = connectorFilled ? C.tealMid : C.border;

            return (
              <React.Fragment key={item.stage || item.id || idx}>
                {/* Step pill ─────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>

                  {/* Circle node */}
                  <Box
                    sx={{
                      width:          16,
                      height:         16,
                      borderRadius:   '50%',
                      border:         `2px solid ${s.nodeBorder}`,
                      bgcolor:        s.nodeBg,
                      color:          s.nodeColor,
                      display:        'flex',
                      alignItems:     'center',
                      justifyContent: 'center',
                      flexShrink:     0,
                      position:       'relative',
                      zIndex:         1,
                      transition:     'all 0.2s',
                      // ✅ FIX: ring rendered correctly; overflow:visible on parent
                      // ensures it's never clipped
                      ...(s.ring && {
                        // Outer ring (green halo) around the small white border.
                        boxShadow: `0 0 0 2px ${C.tealMid}`,
                      }),
                    }}
                  >
                    {item.seq_status === 'completed' && <CheckSVG />}
                  </Box>

                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize:   '12px',
                      fontWeight: s.labelWeight,
                      color:      s.labelColor,
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>

                {/* Connector line ─────────────────────────────────────────── */}
                {!isLast && (
                  <Box
                  sx={{
                    flex:       '0 0 48px',
                    maxWidth:   '48px',
                    height:     '1.5px',
                    minWidth:   12,
                    mx:         '1.25px',
                    bgcolor:    connectorColor,
                    transition: 'background 0.2s',
                  }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {isLeadDeleted ? (
        <Box
          sx={{
            mx: '20px',
            mb: '10px',
            mt: '2px',
            borderRadius: '10px',
            border: '1px solid #FECACA',
            bgcolor: C.redLight,
            px: '12px',
            py: '10px',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: C.redDark, lineHeight: 1.45 }}>
            This lead is <Box component="span" sx={{ fontWeight: 800 }}>deleted</Box>. Pipeline controls are disabled. Use{' '}
            <Box component="span" sx={{ fontWeight: 800 }}>Overview</Box> to <Box component="span" sx={{ fontWeight: 800 }}>Restore</Box> when the lead is recoverable.
          </Typography>
        </Box>
      ) : isJunk ? (
        <Box
          sx={{
            mx: '20px',
            mb: '10px',
            mt: '2px',
            borderRadius: '10px',
            border: '1px solid #FDE68A',
            bgcolor: '#FFFBEB',
            px: '12px',
            py: '10px',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#92400E', lineHeight: 1.45 }}>
            This lead is in <Box component="span" sx={{ fontWeight: 800 }}>Junk</Box>. Reassign and “Change stage” here are disabled. Use{' '}
            <Box component="span" sx={{ fontWeight: 800 }}>Overview</Box> for <Box component="span" sx={{ fontWeight: 800 }}>Move back to Qualified</Box> (when recoverable) or{' '}
            <Box component="span" sx={{ fontWeight: 800 }}>Mark not recoverable</Box>.
          </Typography>
        </Box>
      ) : null}

      {/* ── ROW 2: Status | Assignee | Change Stage button ───────────────── */}
      <Box
        sx={{
          display:    'flex',
          alignItems: 'center',
          gap:        0,
          py:         '8px',
          mb:         '10px',
        }}
      >
        {/* STATUS ─────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display:     'flex',
            alignItems:  'center',
            gap:         '8px',
            pr:          '16px',
            mr:          '16px',
            borderRight: `1px solid ${C.borderSubtle}`,
          }}
        >
          <Typography
            sx={{
              fontSize:      '10px',
              fontWeight:    700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color:         C.slate4,
              whiteSpace:    'nowrap',
            }}
          >
            Status
          </Typography>
          <Box
            sx={{
              fontSize:      '11px',
              fontWeight:    700,
              px:            '10px',
              py:            '3px',
              borderRadius:  '6px',
              bgcolor:       C.blueLight,
              color:         C.blue,
              border:        `1px solid ${C.blueBorder}`,
              letterSpacing: '0.01em',
              lineHeight:    '1.6',
              whiteSpace:    'nowrap',
            }}
          >
            {currentStatusLabel}
          </Box>
        </Box>

        {/* ASSIGNEE ───────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display:     'flex',
            alignItems:  'center',
            gap:         '8px',
            pr:          '16px',
            mr:          '16px',
            borderRight: `1px solid ${C.borderSubtle}`,
          }}
        >
          <Typography
            sx={{
              fontSize:      '10px',
              fontWeight:    700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color:         C.slate4,
              whiteSpace:    'nowrap',
            }}
          >
            Assignee
          </Typography>
          <Tooltip
            title={
              isLeadDeleted
                ? 'Lead deleted — restore from Overview to reassign'
                : isJunk
                  ? 'Junk lead — reassign is disabled; use Overview to move back or mark not recoverable'
                  : 'Click to reassign'
            }
            placement="bottom"
          >
            <Box
              onClick={() => { if (!pipelineActionsLocked) setAssignAgentOpen(true); }}
              sx={{
                display:      'flex',
                alignItems:   'center',
                gap:          '6px',
                cursor:       pipelineActionsLocked ? 'not-allowed' : 'pointer',
                opacity:      pipelineActionsLocked ? 0.65 : 1,
                pl:           '3px',
                pr:           '8px',
                py:           '3px',
                borderRadius: '8px',
                border:       `1px solid ${C.border}`,
                bgcolor:      C.white,
                transition:   'all 0.15s',
                ...(!pipelineActionsLocked && { '&:hover': { borderColor: C.tealMid, bgcolor: C.tealLight } }),
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width:          22,
                  height:         22,
                  borderRadius:   '50%',
                  bgcolor:        agentId ? '#EF4444' : C.borderSubtle,
                  color:          agentId ? C.white : C.slate4,
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  fontSize:       '8px',
                  fontWeight:     700,
                  flexShrink:     0,
                }}
              >
                {agentId ? agentInitials : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize:   '12px',
                  fontWeight: 600,
                  color:      agentId ? C.slate2 : C.slate4,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {agentName || 'Unassigned'}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 14, color: C.slate4 }} />
            </Box>
          </Tooltip>
        </Box>

        {/* CHANGE STAGE / STATUS button ───────────────────────────────────── */}
        <Box
          component="button"
          type="button"
          disabled={pipelineActionsLocked}
          onClick={() => { if (!pipelineActionsLocked) setStatusDialogOpen(true); }}
          sx={{
            display:     'flex',
            alignItems:  'center',
            gap:         '6px',
            bgcolor:     C.teal,
            border:      'none',
            borderRadius:'8px',
            px:          '14px',
            py:          '6px',
            fontSize:    '12px',
            fontWeight:  600,
            color:       C.white,
            fontFamily:  'inherit',
            cursor:      pipelineActionsLocked ? 'not-allowed' : 'pointer',
            whiteSpace:  'nowrap',
            flexShrink:  0,
            boxShadow:   pipelineActionsLocked ? 'none' : '0 2px 8px rgba(14,159,126,0.25)',
            transition:  'all 0.15s',
            opacity:     pipelineActionsLocked ? 0.55 : 1,
            ...(!pipelineActionsLocked && {
              '&:hover': {
                bgcolor:   C.tealDark,
                boxShadow: '0 4px 12px rgba(14,159,126,0.3)',
                transform: 'translateY(-1px)',
              },
            }),
          }}
        >
          <EditSVG />
          Change Stage / Status
        </Box>
      </Box>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      {agentId ? (
        <ReassignAgent
          open={assignAgentOpen}
          onClose={() => setAssignAgentOpen(false)}
          selectedLeadIds={[lead?.id]}
          currentAgent={agent}
          currentStage={lead?.stage}
          currentStatus={lead?.status}
          leadName={lead?.contact_info?.name || ''}
          leadId={lead?.id}
          onComplete={() => { setAssignAgentOpen(false); fetchLeadDetails(lead?.id); }}
        />
      ) : (
        <AssignAgent
          open={assignAgentOpen}
          onClose={() => setAssignAgentOpen(false)}
          selectedLeadIds={[lead?.id]}
          currentAgent={null}
          onAssign={() => { setAssignAgentOpen(false); fetchLeadDetails(lead?.id); }}
        />
      )}

      <UpdateStatus
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        leadIds={[lead?.id]}
        currentStatus={currentStatus}
        currentStage={currentStage}
        leadName={lead?.contact_info?.name || ''}
        leadId={lead?.id}
        projectUnits={projectUnits}
        onComplete={() => { setStatusDialogOpen(false); fetchLeadDetails(lead?.id); }}
      />
    </Box>
  );
};

export default LeadPipeline;
